import React, { useState, useEffect } from 'react';
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { X, UploadCloud, CheckCircle, Info, FileText, AlertTriangle, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { CommanServices } from '@/services/commanServices';
import { parseTimestamp } from '@/uitilites/utility';
import ExtensiveAnalysisModal from './ExtensiveAnalysisModal';

const INPUT_CLS = "flex h-10 w-full rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900 transition-all";
const TEXTAREA_CLS = "block w-full rounded-sm border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-emerald-900 focus:border-emerald-900 transition-all resize-y";
const FIELD_LABEL = "mb-1.5 block text-sm font-medium tracking-tight text-slate-700";

const autoSummaryPayload = (response) => {
  const candidates = [response?.data?.data, response?.data, response];
  return candidates.find((candidate) => candidate && typeof candidate === 'object' && (
    typeof candidate.summary === 'string'
    || candidate.reviewKind
    || candidate.review_kind
  )) || null;
};

const ResolutionModal = ({ incident, editingItem, onClose, onSuccess }) => {
  const incidentReviewKind = (incident?.is_exercise === true || incident?.is_scenario === true) ? 'aar' : 'pir';
  const [reviewKind, setReviewKind] = useState(incidentReviewKind);
  const isExerciseReview = reviewKind === 'aar';
  const [formData, setFormData] = useState({
    resolutionStatus: 'Resolved Successfully',
    customResolutionStatus: '',
    resolutionSummary: '',
    rootCause: '',
    actionsTaken: '',
    lessonsLearned: '',
    finalSeverity: incident?.incident_severity || '',
    resolvedBy: null,
    attachments: [],
    relatedIncidents: '',
    afterActionReview: ''
  });

  const [timelineData, setTimelineData] = useState([]);
  const [showExtensive, setShowExtensive] = useState(false);
  const [extensiveReport, setExtensiveReport] = useState('');

  const [appUsers, setAppUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [autoSummary, setAutoSummary] = useState('');
  const [timeToResolution, setTimeToResolution] = useState('');
  const [aiSummaryLoading, setAiSummaryLoading] = useState(false);

  const resolutionTypes = [
    'Resolved Successfully',
    'False Alarm',
    'Duplicate Incident',
    'Escalated to External Agency',
    'Cannot Resolve',
    'Custom'
  ];

  useEffect(() => {
    setReviewKind(incidentReviewKind);
  }, [incidentReviewKind]);

  // Time to resolution = activation → now (or resolved/completed timestamp).
  // parseTimestamp treats naive backend timestamps as UTC — plain `new Date()`
  // read them as local time, pushing the start into the future and showing 0m.
  useEffect(() => {
    const startSrc = incident?.activated_at || incident?.created_at;
    if (startSrc) {
      const startTime = parseTimestamp(startSrc);
      // resolved_at is only the incident's finalization time once the status says
      // so — new incidents carry a creation-time resolved_at ("severity resolved")
      // which pushed the end before the start and showed 0m.
      const isFinalized = ['resolved', 'closed', 'completed'].includes(String(incident?.status || '').toLowerCase());
      const endSrc = isFinalized ? (incident?.resolved_at || incident?.completed_at) : null;
      const endTime = endSrc ? parseTimestamp(endSrc) : new Date();
      if (!startTime || !endTime) return;
      const diffMins = Math.max(0, Math.floor((endTime - startTime) / 60000));
      const hours = Math.floor(diffMins / 60);
      const mins = diffMins % 60;
      setTimeToResolution(hours > 0 ? `${hours}h ${mins}m` : `${mins}m`);
    }
  }, [incident]);

  // App users
  useEffect(() => {
    (async () => {
      try {
        const response = await CommanServices.getAppUsers();
        if (response.status) setAppUsers(await response.data);
      } catch (e) {
        console.error('Error fetching app users:', e);
      }
    })();
  }, []);

  // Template auto-summary fallback
  useEffect(() => {
    const generateAutoSummary = async () => {
      try {
        const response = await CommanServices.getincidentTimeline(incident.id);
        if (response.status) {
          const timeline = await response.data;
          setTimelineData(timeline);
          const statusUpdates = timeline.filter(t => t.group === 'status_update' || t.group === 'status_update_2');
          const responseActions = timeline.filter(t => t.group === 'response_action');
          const acknowledgments = timeline.filter(t => t.group === 'acknowledgment');
          let summary = `${isExerciseReview ? 'Exercise Summary' : 'Incident Resolution Summary'}:\n`;
          summary += `Type: ${incident.incident_type || 'N/A'}\n`;
          summary += `Location: ${incident.location_names?.join(', ') || incident.location?.join(', ') || 'N/A'}\n`;
          summary += `Acknowledgments: ${acknowledgments.length}\n`;
          summary += `Status Updates: ${statusUpdates.length}\n`;
          summary += `Response Actions: ${responseActions.length}\n`;
          if (statusUpdates.length > 0) {
            const latestUpdate = statusUpdates[statusUpdates.length - 1];
            summary += `\nLatest Status: ${latestUpdate.details || latestUpdate.title}`;
          }
          setAutoSummary(summary);
        }
      } catch (e) {
        console.error('Error generating auto summary:', e);
      }
    };
    if (incident && !isEditMode) generateAutoSummary();
  }, [incident, isEditMode, isExerciseReview]);

  // Edit-mode hydration
  useEffect(() => {
    if (editingItem) {
      setIsEditMode(true);
      const meta = editingItem.meta || {};
      setFormData({
        resolutionStatus: meta.resolutionStatus || 'Resolved Successfully',
        customResolutionStatus: meta.customResolutionStatus || '',
        resolutionSummary: meta.resolutionSummary || editingItem.details || '',
        rootCause: meta.rootCause || '',
        actionsTaken: meta.actionsTaken || '',
        lessonsLearned: meta.lessonsLearned || '',
        finalSeverity: meta.finalSeverity || incident?.incident_severity || '',
        resolvedBy: editingItem.actor || null,
        attachments: editingItem.attachments || [],
        relatedIncidents: meta.relatedIncidents || '',
        afterActionReview: meta.afterActionReview || ''
      });
      const existingEA = (editingItem.attachments || []).find(a => a?.isExtensiveAnalysis && a?.content);
      if (existingEA) setExtensiveReport(existingEA.content);
    } else {
      setFormData(prev => ({ ...prev, resolvedBy: { id: 'current_user', name: 'Current User', type: 'user' } }));
    }
  }, [editingItem, incident]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploadingFiles(true);
    setError('');
    try {
      const uploadedFiles = files.map(file => ({
        filename: file.name,
        url: URL.createObjectURL(file),
        type: file.type,
        size: file.size
      }));
      setFormData(prev => ({ ...prev, attachments: [...prev.attachments, ...uploadedFiles] }));
    } catch (e) {
      console.error('Error processing files:', e);
      setError('Failed to process files. Please try again.');
    } finally {
      setUploadingFiles(false);
      if (e.target) e.target.value = '';
    }
  };

  const handleRemoveAttachment = (index) => {
    setFormData(prev => ({ ...prev, attachments: prev.attachments.filter((_, i) => i !== index) }));
  };

  // Build/refresh the Extensive Analysis report as an attachment (saved straight into the widget).
  const upsertExtensiveAttachment = (md) => {
    const blob = new Blob([md], { type: 'text/markdown' });
    const attachment = {
      filename: `Extensive_Analysis_${incident?.id || 'report'}.md`,
      url: URL.createObjectURL(blob),
      type: 'text/markdown',
      size: blob.size,
      content: md,
      isExtensiveAnalysis: true,
    };
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments.filter(a => !a?.isExtensiveAnalysis), attachment],
    }));
  };

  // Called when the Extensive Analysis modal generates a report: persist it + auto-attach.
  const handleExtensiveReport = (md) => {
    setExtensiveReport(md);
    upsertExtensiveAttachment(md);
  };

  // Attachments-section button: if a report already exists, attach it; otherwise open the modal.
  const handleExtensiveButton = () => {
    if (extensiveReport && extensiveReport.trim()) {
      upsertExtensiveAttachment(extensiveReport);
      toast.success('Extensive Analysis report saved to attachments');
    } else {
      setShowExtensive(true);
    }
  };

  const handleUseAutoSummary = async () => {
    setAiSummaryLoading(true);
    try {
      const res = await CommanServices.incidentAutoSummary(incident.id);
      const payload = autoSummaryPayload(res);
      const serverReviewKind = String(payload?.reviewKind || payload?.review_kind || '').toLowerCase();
      if (serverReviewKind === 'pir' || serverReviewKind === 'aar') setReviewKind(serverReviewKind);
      const text = String(payload?.summary || '').trim();
      if (res?.status !== false && text) {
        setFormData(prev => ({ ...prev, resolutionSummary: text }));
        toast.success('AI summary generated');
      } else if (autoSummary) {
        setFormData(prev => ({ ...prev, resolutionSummary: autoSummary }));
        toast.message('Used template summary (AI unavailable)');
      } else {
        toast.error('Could not generate a summary');
      }
    } catch (e) {
      if (autoSummary) {
        setFormData(prev => ({ ...prev, resolutionSummary: autoSummary }));
        toast.message('Used template summary (AI unavailable)');
      } else {
        toast.error('Could not generate a summary');
      }
    } finally {
      setAiSummaryLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!isEditMode && incident?.map_projection && !incident.map_projection.official) {
      setError('Map this incident to an official Locations & Zones GPS before resolving it.');
      return;
    }
    if (!formData.resolutionSummary.trim()) { setError('Please enter resolution summary'); return; }
    if (formData.resolutionStatus === 'Custom' && !formData.customResolutionStatus.trim()) { setError('Please enter custom resolution status'); return; }
    if (!formData.resolvedBy) { setError('Please select who resolved this incident'); return; }

    setLoading(true);
    setError('');
    try {
      const finalStatus = formData.resolutionStatus === 'Custom' ? formData.customResolutionStatus : formData.resolutionStatus;

      const updatePayload = isEditMode ? {
        ...editingItem,
        timestamp: editingItem.timestamp,
        title: `Resolution: ${finalStatus}`,
        details: formData.resolutionSummary,
        actor: formData.resolvedBy,
        attachments: formData.attachments,
        chips: [finalStatus, `${timeToResolution}`],
        meta: {
          ...(editingItem.meta || {}),
          resolutionStatus: formData.resolutionStatus,
          customResolutionStatus: formData.customResolutionStatus,
          resolutionSummary: formData.resolutionSummary,
          rootCause: formData.rootCause,
          actionsTaken: formData.actionsTaken,
          lessonsLearned: formData.lessonsLearned,
          finalSeverity: formData.finalSeverity,
          timeToResolution,
          relatedIncidents: formData.relatedIncidents,
          afterActionReview: formData.afterActionReview,
          history: [
            ...(editingItem.meta?.history || []),
            { timestamp: new Date().toISOString(), actor: formData.resolvedBy.name, action: 'Updated Resolution', changes: { resolutionStatus: finalStatus } }
          ]
        }
      } : {
        incident_id: incident.id,
        group: 'resolution',
        timestamp: new Date().toISOString(),
        actor: formData.resolvedBy,
        title: `Resolution: ${finalStatus}`,
        details: formData.resolutionSummary,
        chips: [finalStatus, `⏱️ ${timeToResolution}`],
        attachments: formData.attachments,
        meta: {
          resolutionStatus: formData.resolutionStatus,
          customResolutionStatus: formData.customResolutionStatus,
          resolutionSummary: formData.resolutionSummary,
          rootCause: formData.rootCause,
          actionsTaken: formData.actionsTaken,
          lessonsLearned: formData.lessonsLearned,
          finalSeverity: formData.finalSeverity,
          timeToResolution,
          relatedIncidents: formData.relatedIncidents,
          afterActionReview: formData.afterActionReview,
          history: []
        }
      };

      if (isEditMode) {
        const res = await CommanServices.updatePutIncidentTimeline(incident.id, editingItem.id, updatePayload);
        if (!res.status) throw new Error('Failed to save status update');
      } else {
        const response = await CommanServices.addincidentTimeline(incident.id, updatePayload);
        if (!response.status) throw new Error(response?.message || 'Failed to save resolution');
      }

      // Creating the resolution card is the authoritative lifecycle transition.
      // Persist the optional final-severity annotation separately so an empty
      // legacy value cannot turn a successful resolution into a validation error.
      if (!isEditMode && formData.finalSeverity) {
        const statusResponse = await CommanServices.updatePatchIncidents(incident.id, {
          resolved_severity: formData.finalSeverity
        });
        if (statusResponse?.status === false) throw new Error(statusResponse?.message || 'Failed to resolve incident');
      }

      onSuccess && onSuccess();
      onClose();
    } catch (e) {
      console.error('Error saving resolution:', e);
      setError(e.message || 'Failed to save resolution');
    } finally {
      setLoading(false);
    }
  };

  const extensiveSupplementalMaterial = [
    `# ${incident?.incident_type || ''} — ${incident?.event || ''} — ${incident?.trigger || ''}`,
    `**${isExerciseReview ? 'Exercise' : 'Incident'} ID:** ${incident?.id || ''}`,
    `**Status:** ${incident?.status || ''}`,
    `**Time to Resolution:** ${timeToResolution}`,
    formData.resolutionSummary
      ? `\n## ${isExerciseReview ? 'Exercise Summary' : 'Incident Resolution Summary'}\n${formData.resolutionSummary}`
      : '',
    formData.rootCause ? `\n## Root Cause Analysis\n${formData.rootCause}` : '',
    formData.actionsTaken ? `\n## Actions Taken\n${formData.actionsTaken}` : '',
    formData.lessonsLearned ? `\n## Lessons Learned\n${formData.lessonsLearned}` : '',
  ].filter(Boolean).join('\n');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4" data-testid="resolution-modal">
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-sm border border-slate-200 bg-white shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-start justify-between border-b border-slate-100 bg-white px-8 py-6">
          <div>
            <h2 className="flex items-center gap-2 text-2xl font-semibold tracking-tight text-slate-900">
              <span className="flex h-8 w-8 items-center justify-center rounded-sm bg-emerald-900 text-white"><CheckCircle className="h-5 w-5" /></span>
              {isEditMode ? 'Edit Resolution' : 'Resolve Incident'}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm font-medium text-slate-500">
              <span>Incident: <span className="font-semibold text-slate-700">{incident?.id}</span></span>
              <span className="inline-flex items-center rounded-sm border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                Time to Resolution: {timeToResolution || '—'}
              </span>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close resolution dialog" className="rounded-sm p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-900" data-testid="resolution-close-btn">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-1 flex-col gap-9 overflow-y-auto p-8">
          {error && (
            <div className="flex items-center gap-2 rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4" /> {error}
            </div>
          )}

          {/* Meta fields */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div>
              <Label htmlFor="resolvedBy" className={FIELD_LABEL}>Resolved By *</Label>
              <Select
                value={formData.resolvedBy?.id || ''}
                onValueChange={(selectedUserId) => {
                  const selected = appUsers.find(u => u.id === selectedUserId);
                  if (selected) setFormData(prev => ({ ...prev, resolvedBy: { id: selected.id, name: selected.name || selected.email, type: 'user' } }));
                }}
              >
                <SelectTrigger className="rounded-sm border-slate-200 focus:ring-emerald-900" data-testid="resolved-by-select"><SelectValue placeholder="Select user..." /></SelectTrigger>
                <SelectContent>
                  {appUsers.map(user => <SelectItem key={user.id} value={user.id}>{user.name || user.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="resolutionStatus" className={FIELD_LABEL}>Resolution Status *</Label>
              <Select value={formData.resolutionStatus} onValueChange={(value) => setFormData(prev => ({ ...prev, resolutionStatus: value }))}>
                <SelectTrigger className="rounded-sm border-slate-200 focus:ring-emerald-900" data-testid="resolution-status-select"><SelectValue placeholder="Select resolution status..." /></SelectTrigger>
                <SelectContent>
                  {resolutionTypes.map(type => <SelectItem key={type} value={type}>{type}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {formData.resolutionStatus === 'Custom' && (
              <div className="md:col-span-2">
                <Label htmlFor="customResolutionStatus" className={FIELD_LABEL}>Custom Resolution Status *</Label>
                <Input
                  id="customResolutionStatus"
                  value={formData.customResolutionStatus}
                  onChange={(e) => setFormData(prev => ({ ...prev, customResolutionStatus: e.target.value }))}
                  placeholder="Enter custom resolution status..."
                  className={INPUT_CLS}
                  data-testid="custom-resolution-input"
                />
              </div>
            )}
          </div>

          {/* Resolution/exercise summary */}
          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label htmlFor="resolutionSummary" className="text-sm font-semibold text-slate-900">
                {isExerciseReview ? 'Exercise Summary *' : 'Incident Resolution Summary *'}
              </Label>
              {!isEditMode && (
                <button
                  type="button"
                  onClick={handleUseAutoSummary}
                  disabled={aiSummaryLoading}
                  data-testid="ai-auto-summary-btn"
                  className="inline-flex items-center gap-1.5 rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-emerald-900 transition-colors hover:bg-emerald-100 disabled:opacity-60"
                >
                  {aiSummaryLoading ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Generating…</> : <><Sparkles className="h-3.5 w-3.5 text-emerald-600" /> AI Auto-Summary</>}
                </button>
              )}
            </div>
            <textarea
              id="resolutionSummary"
              value={formData.resolutionSummary}
              onChange={(e) => setFormData(prev => ({ ...prev, resolutionSummary: e.target.value }))}
              placeholder={isExerciseReview
                ? "Summarize how the exercise performed — objectives, response timeline, SLA and acknowledgment performance, and key takeaways. Click 'AI Auto-Summary' to draft this from the exercise evidence, then edit as needed."
                : "Summarize the operational incident, its impact, response timeline, decisions and actions taken, resolution outcome, and remaining follow-up. Click 'AI Auto-Summary' to draft this from the incident evidence, then edit as needed."}
              className={`${TEXTAREA_CLS} min-h-[110px]`}
              rows={4}
              data-testid="resolution-summary-input"
            />
          </div>

          {/* Post-mortem group */}
          <div className="space-y-6 rounded-sm border border-slate-100 bg-slate-50/50 p-6">
            <h3 className="border-b border-slate-200 pb-2 text-sm font-semibold uppercase tracking-wider text-slate-800">
              {isExerciseReview ? 'After-Action Review' : 'Post-Incident Review'}
            </h3>
            <div>
              <Label htmlFor="rootCause" className={FIELD_LABEL}>Root Cause Analysis</Label>
              <textarea
                id="rootCause"
                value={formData.rootCause}
                onChange={(e) => setFormData(prev => ({ ...prev, rootCause: e.target.value }))}
                placeholder={isExerciseReview
                  ? 'What underlying conditions affected exercise performance?'
                  : 'What was the underlying cause of this incident?'}
                className={`${TEXTAREA_CLS} min-h-[84px]`}
                rows={3}
                data-testid="root-cause-input"
              />
            </div>
            <div>
              <Label htmlFor="actionsTaken" className={FIELD_LABEL}>Actions Taken Summary</Label>
              <textarea
                id="actionsTaken"
                value={formData.actionsTaken}
                onChange={(e) => setFormData(prev => ({ ...prev, actionsTaken: e.target.value }))}
                placeholder={isExerciseReview
                  ? 'Summarize the actions taken and decisions made during the exercise...'
                  : 'Summarize the actions taken to resolve this incident...'}
                className={`${TEXTAREA_CLS} min-h-[84px]`}
                rows={3}
                data-testid="actions-taken-input"
              />
            </div>
            <div>
              <Label htmlFor="lessonsLearned" className={FIELD_LABEL}>Lessons Learned</Label>
              <textarea
                id="lessonsLearned"
                value={formData.lessonsLearned}
                onChange={(e) => setFormData(prev => ({ ...prev, lessonsLearned: e.target.value }))}
                placeholder={isExerciseReview
                  ? 'What lessons were learned, and how should future exercises or response plans improve?'
                  : 'What lessons were learned? How can we prevent this in the future?'}
                className={`${TEXTAREA_CLS} min-h-[84px]`}
                rows={3}
                data-testid="lessons-learned-input"
              />
            </div>
          </div>

          {/* Attachments */}
          <div>
            <div className="mb-4 flex items-center justify-between">
              <Label className="text-sm font-semibold text-slate-900">Attachments (Reports, Photos)</Label>
              <button
                type="button"
                onClick={handleExtensiveButton}
                data-testid="open-extensive-analysis-btn"
                className="inline-flex items-center gap-1.5 rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-emerald-900 shadow-sm transition-colors hover:bg-emerald-100"
                title={extensiveReport ? 'Save the generated Extensive Analysis report to attachments' : 'Generate an Extensive Analysis report'}
              >
                <Sparkles className="h-3.5 w-3.5 text-emerald-600" /> Extensive Analysis
              </button>
            </div>

            <label
              htmlFor="resolution-file-input"
              className="group flex cursor-pointer flex-col items-center justify-center gap-3 rounded-sm border-2 border-dashed border-slate-200 bg-slate-50 p-8 transition-colors hover:border-slate-300 hover:bg-slate-100"
            >
              <UploadCloud className="h-8 w-8 text-slate-400 transition-colors group-hover:text-emerald-900" />
              <div className="text-center">
                <p className="text-sm font-semibold text-slate-700">{uploadingFiles ? 'Uploading…' : 'Drop files or click to upload'}</p>
                <p className="text-xs text-slate-500">Reports, photos and documents</p>
              </div>
              <input
                id="resolution-file-input"
                type="file"
                multiple
                onChange={handleFileUpload}
                className="hidden"
                disabled={uploadingFiles}
                data-testid="resolution-file-input"
              />
            </label>

            {formData.attachments.length > 0 && (
              <div className="mt-4 flex flex-col gap-2" data-testid="resolution-attachment-list">
                {formData.attachments.map((attachment, index) => (
                  <div key={index} className="flex items-center justify-between rounded-sm border border-slate-200 bg-white p-3 transition-colors hover:border-slate-300">
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <FileText className="h-4 w-4 flex-shrink-0 text-emerald-700" />
                      <span className="truncate text-sm font-medium text-slate-700">{attachment.filename}</span>
                      {attachment.isExtensiveAnalysis && (
                        <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-sm bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800">
                          <Sparkles className="h-2.5 w-2.5" /> AI
                        </span>
                      )}
                      {attachment.size > 0 && <span className="flex-shrink-0 text-xs text-slate-400">({Math.round(attachment.size / 1024)}KB)</span>}
                    </div>
                    <button type="button" onClick={() => handleRemoveAttachment(index)} className="ml-2 p-1 text-slate-400 transition-colors hover:text-red-600" data-testid={`remove-attachment-${index}`}>
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 z-10 flex items-center justify-between border-t border-slate-100 bg-slate-50 px-8 py-5">
          <div className="flex items-center gap-2 text-sm font-medium text-slate-500">
            <Info className="h-4 w-4" /> Incident will be marked as <span className="font-semibold text-slate-700">Resolved</span>
          </div>
          <div className="flex items-center gap-3">
            <Button type="button" variant="ghost" onClick={onClose} className="rounded-sm text-slate-500 hover:text-slate-900" data-testid="resolution-cancel-btn">Cancel</Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="rounded-sm bg-emerald-900 px-6 font-medium text-white shadow-sm hover:bg-emerald-800"
              data-testid="resolution-submit-btn"
            >
              {loading ? 'Saving...' : <><CheckCircle className="mr-2 h-4 w-4" />{isEditMode ? 'Update Resolution' : 'Resolve Incident'}</>}
            </Button>
          </div>
        </div>
      </div>

      {showExtensive && (
        <ExtensiveAnalysisModal
          onClose={() => setShowExtensive(false)}
          title={`Extensive Analysis — ${incident?.id || ''}`}
          incidentId={incident?.id}
          reviewKind={reviewKind}
          initialReport={extensiveReport}
          onReportChange={handleExtensiveReport}
          defaultObjective={isExerciseReview
            ? "Evaluate this exercise's end-to-end performance and produce a full, decision-ready After-Action Review."
            : "Evaluate this real incident's end-to-end response and produce a full, decision-ready Post-Incident Review."}
          defaultMaterial={extensiveSupplementalMaterial}
          contextSupplementalMaterial={extensiveSupplementalMaterial}
        />
      )}
    </div>
  );
};

export default ResolutionModal;
